<template>
  <div :class="[$options.name, barClassList]">
    <div :class="[$options.name] + '__bar-container'">
      <span :class="[$options.name] + '__bar'" :style="{ width: `${progressValue}%`, maxWidth: '100%' }"></span>
    </div>
    <EdroneBadge
      v-if="props.badge"
      :class="[$options.name] + '__badge'"
      :disabled="props.disabled"
      :label="badgeValue"
      :type="props.type"
      size="small"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, watch, ref } from "vue";
import EdroneBadge from "edrone-components/EdroneBadge/EdroneBadge.vue";

export default defineComponent({
  name: "EdroneProgressBar",
  components: { EdroneBadge },
  props: {
    /** Progress of process, used to calculate progress bar width (number between 0 to 100) - 100 means 100% */
    value: {
      type: Number,
      default: 0,
    },
    /** Color of the bar */
    type: {
      type: String as PropType<"default" | "info" | "success" | "warning" | "error">,
      default: "default",
    },
    /**
     * If you pass a false then the badge will not be shown.
     * If you pass true then the badge will show percentage %.
     * If you pass a string, then you can pass anything as a badge text.
     */
    badge: {
      type: [Boolean, String],
      default: true,
    },
    /** Whether the progress bar should change color to dimmer */
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const progressValue = ref(props.value);
    const barClassList = computed(() => {
      return {
        "EdroneProgressBar--info": props.type === "info",
        "EdroneProgressBar--success": props.type === "success",
        "EdroneProgressBar--warn": props.type === "warning",
        "EdroneProgressBar--error": props.type === "error",
        "EdroneProgressBar--disabled": props.disabled,
      };
    });

    watch(
      () => props.value,
      () => {
        if (props.value > 100) {
          progressValue.value = 100;
        } else if (props.value < 0) {
          progressValue.value = 0;
        } else {
          progressValue.value = props.value;
        }
      }
    );

    const badgeValue = computed(() => {
      if (typeof props.badge === "boolean" && props.badge) {
        return `${progressValue.value}%`;
      } else if (typeof props.badge === "string") {
        return props.badge;
      }
      return undefined;
    });

    return {
      props,
      barClassList,
      progressValue,
      badgeValue,
    };
  },
});
</script>

<style scoped>
@reference "vue-app/assets/scss/main.css";

.EdroneProgressBar {
  display: flex;
  align-items: center;
}

.EdroneProgressBar .EdroneProgressBar__bar-container {
  width: 100%;
  @apply tw:bg-default-100 tw:h-2 tw:rounded-lg;
}

.EdroneProgressBar .EdroneProgressBar__bar {
  width: 100%;
  display: block;
  position: relative;
  transition: width 0.3s ease-in-out;
  @apply tw:bg-default-800 tw:h-2 tw:rounded-lg;
}

.EdroneProgressBar__badge {
  @apply tw:ml-2;
}

.EdroneProgressBar--info .EdroneProgressBar__bar-container {
  @apply tw:bg-blue-100;
}

.EdroneProgressBar--info .EdroneProgressBar__bar {
  @apply tw:bg-blue-500;
}

.EdroneProgressBar--success .EdroneProgressBar__bar-container {
  @apply tw:bg-green-100;
}

.EdroneProgressBar--success .EdroneProgressBar__bar {
  @apply tw:bg-green-500;
}

.EdroneProgressBar--warn .EdroneProgressBar__bar-container {
  @apply tw:!bg-yellow-100;
}

.EdroneProgressBar--warn .EdroneProgressBar__bar {
  @apply tw:!bg-yellow-500;
}

.EdroneProgressBar--error .EdroneProgressBar__bar-container {
  @apply tw:bg-red-100;
}

.EdroneProgressBar--error .EdroneProgressBar__bar {
  @apply tw:bg-red-500;
}

.EdroneProgressBar--disabled .EdroneProgressBar__bar-container {
  @apply tw:bg-default-000;
}

.EdroneProgressBar--disabled .EdroneProgressBar__bar {
  @apply tw:bg-default-500;
}

.EdroneProgressBar--disabled.EdroneProgressBar--info .EdroneProgressBar__bar-container {
  @apply tw:bg-blue-000;
}

.EdroneProgressBar--disabled.EdroneProgressBar--info .EdroneProgressBar__bar {
  @apply tw:bg-blue-100;
}

.EdroneProgressBar--disabled.EdroneProgressBar--success .EdroneProgressBar__bar-container {
  @apply tw:bg-green-000;
}

.EdroneProgressBar--disabled.EdroneProgressBar--success .EdroneProgressBar__bar {
  @apply tw:bg-green-100;
}

.EdroneProgressBar--disabled.EdroneProgressBar--warn .EdroneProgressBar__bar-container {
  @apply tw:!bg-yellow-000;
}

.EdroneProgressBar--disabled.EdroneProgressBar--warn .EdroneProgressBar__bar {
  @apply tw:!bg-yellow-100;
}

.EdroneProgressBar--disabled.EdroneProgressBar--error .EdroneProgressBar__bar-container {
  @apply tw:bg-red-000;
}

.EdroneProgressBar--disabled.EdroneProgressBar--error .EdroneProgressBar__bar {
  @apply tw:bg-red-100;
}
</style>
